import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary, Drawer, Icon } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { useMouRenewals } from '@/lib/queries';

interface MoUDoc {
  _id: string;
  partner: { _id: string; name: string; industry?: string; headquarters?: { city?: string } } | string;
  status: string;
  kind?: string;
  scope?: { colleges?: string[] };
  term?: { start?: string; end?: string };
  signatures?: Array<{ role: string; signed?: boolean; signedByName?: string; signedAt?: string }>;
  changedClauses?: string[];
  createdAt?: string;
  renewalOf?: string;
}

/**
 * /group/mous/:id — direct port of kollez/pages/group/mou.html.
 *   .paper agreement with change-highlighted clauses + right aside (changes summary + signatures + Aadhaar eSign flow).
 */
function fmtDay(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function GroupMouDetailPage() {
  const { id } = useParams();
  const [changesOpen, setChangesOpen] = useState(false);

  const query = useQuery({
    queryKey: ['group', 'mou', id],
    queryFn: () => apiGet<{ mou: MoUDoc }>(`/group/mous/${id}`),
    enabled: !!id,
  });
  const mou = query.data?.mou;
  const renewals = useMouRenewals(id);
  const chain = renewals.data?.chain ?? [];
  const seedIdx = renewals.data?.seedIndex ?? 0;
  const gaps = renewals.data?.gaps ?? [];
  const partner = typeof mou?.partner === 'object' ? mou.partner : null;
  const partnerName = partner?.name ?? 'Partner';
  const groupSignature = (mou?.signatures ?? []).find((s) => s.role === 'tenant');
  const signed = !!groupSignature?.signed;
  const collegeCount = mou?.scope?.colleges?.length ?? 0;
  const termStart = mou?.term?.start ? new Date(mou.term.start) : null;
  const termEnd = mou?.term?.end ? new Date(mou.term.end) : null;

  return (
    <div className="stack">
      <style>{`
        .mou { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .mou { grid-template-columns: minmax(0,1fr) 340px; } .mou-side { position: sticky; top: 76px; } }
        .paper h3 { font-size: 1rem; margin: 18px 0 6px; font-weight: 600; }
        .chg { background: rgb(214 170 40 / .22); border-radius: 2px; padding: 0 2px; }
        .sig { height: 56px; border-bottom: 1px solid #1a1d2b; font-family: "Newsreader", Georgia, serif; font-style: italic; font-size: 1.6rem; display: flex; align-items: end; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            {mou?._id?.slice(-8) ?? 'MoU'} · {mou?.kind ?? 'agreement'}{mou?.renewalOf ? ` · renewal of ${String(mou.renewalOf).slice(-8)}` : ''}
          </p>
          <h1 className="h-page">
            {partnerName} · {mou?.kind === 'RECRUITMENT' ? 'campus recruitment' : 'partnership'}
          </h1>
          <p className="subhead">
            {mou?.status ? `Status: ${mou.status}` : 'Loading MoU status…'}
            {mou?.changedClauses && mou.changedClauses.length > 0 &&
              ` · ${mou.changedClauses.length} change${mou.changedClauses.length === 1 ? '' : 's'} from the prior agreement`}
          </p>
        </div>
      </div>

      <div className="mou">
        <div className="paper" style={{ padding: '30px 32px', boxShadow: 'var(--shadow-md)' }}>
          <p style={{ color: '#5b6070', fontSize: 13 }}>
            Memorandum of Understanding · {termStart ? fmtDay(termStart.toISOString()) : '—'} – {termEnd ? fmtDay(termEnd.toISOString()) : '—'}
          </p>
          <p>
            This agreement is between the tenant (the "Group") and <b>{partnerName}</b> (the "Company").
          </p>
          <h3>1. Scope</h3>
          <p>
            The Company may conduct campus recruitment at the <span className="var">{collegeCount} college{collegeCount === 1 ? '' : 's'}</span> listed
            in Schedule A through kollez.ai.
          </p>
          <h3>2. Process</h3>
          <p>Clause text drafting UI is deferred. See the summary above for scope, kind, and status; open <b>Changes from the last agreement</b> to review renegotiated clauses.</p>
          <h3>3. Term</h3>
          <p>
            {termStart && termEnd
              ? `${fmtDay(termStart.toISOString())} to ${fmtDay(termEnd.toISOString())}. Renewable by mutual written consent.`
              : 'Term dates not set. Fill via Renew or Draft.'}
          </p>
          <div className="grid grid-cols-2 gap-8 mt-10">
            {(mou?.signatures ?? []).map((sig, i) => (
              <div key={i}>
                {sig.signed && sig.signedByName ? (
                  <div className="sig">{sig.signedByName}</div>
                ) : (
                  <div className="sig" style={{ color: '#8a8fa0', fontStyle: 'normal', fontSize: '.9rem' }}>Awaiting signature</div>
                )}
                <p style={{ fontSize: 13, marginTop: 6 }}>
                  {sig.role === 'partner' ? `For ${partnerName}` : 'For the Group'}
                  {sig.signed && sig.signedAt ? <><br /><i>Signed {fmtDay(sig.signedAt)}</i></> : null}
                </p>
              </div>
            ))}
            {(!mou?.signatures || mou.signatures.length === 0) && (
              <p className="cell-sub" style={{ gridColumn: '1 / -1' }}>
                No signature slots yet — sign the MoU first via <b>Sign as Group</b> to seed them.
              </p>
            )}
          </div>
        </div>

        <aside className="mou-side grid gap-4">
          {mou?.changedClauses && mou.changedClauses.length > 0 && (
            <div className="card card-pad grid gap-3">
              <h2 className="h-section">Changes from the last agreement</h2>
              {mou.changedClauses.map((c, i) => (
                <div key={i} className="qcheck warn"><Icon name="pencil" size={14} /><span>{c}</span></div>
              ))}
            </div>
          )}

          {chain.length > 1 && (
            <div className="card card-pad grid gap-3">
              <h2 className="h-section">Renewal chain</h2>
              <ol className="timeline">
                {chain.map((node, i) => {
                  const isSeed = i === seedIdx;
                  const term = node.term ?? {};
                  const startsAt = term.startsAt ?? term.start;
                  const endsAt = term.endsAt ?? term.end;
                  return (
                    <li key={node._id} data-state={isSeed ? 'current' : 'done'}>
                      <span className="t-dot" />
                      <div className="t-title">
                        {node.mouNumber ?? `MoU ${node._id.slice(-8)}`}
                        {isSeed && <span className="badge ml-2">this MoU</span>}
                        {node.status && <span className="badge ml-2">{node.status}</span>}
                      </div>
                      <div className="t-meta">
                        {fmtDay(startsAt)} → {fmtDay(endsAt)}
                        {node.changedClauses && node.changedClauses.length > 0 && (
                          <> · {node.changedClauses.length} changed clause{node.changedClauses.length === 1 ? '' : 's'}</>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {gaps.some((g) => g.daysGap && g.daysGap > 0) && (
                <p className="helper m-0">
                  {gaps
                    .filter((g) => g.daysGap && g.daysGap > 0)
                    .map((g) => `${g.daysGap}-day gap between renewals`)
                    .join(' · ')}
                </p>
              )}
            </div>
          )}

          <div className="card card-pad grid gap-3">
            <h2 className="h-section">Signatures</h2>
            <ol className="timeline">
              {(mou?.signatures ?? []).map((sig, i) => (
                <li key={i} data-state={sig.signed ? 'done' : 'current'}>
                  <span className="t-dot" />
                  <div className="t-title">
                    {sig.signed ? `Signed by ${sig.signedByName ?? sig.role}` : `${sig.role === 'partner' ? partnerName : 'Group'} signature`}
                  </div>
                  <div className="t-meta">
                    {sig.signed && sig.signedAt ? fmtDay(sig.signedAt) : 'awaiting'}
                  </div>
                </li>
              ))}
              {(!mou?.signatures || mou.signatures.length === 0) && (
                <li data-state="current"><span className="t-dot" /><div className="t-title">Not yet signed</div><div className="t-meta">Seed slots by signing</div></li>
              )}
            </ol>
            {!signed && (
              <>
                <button
                  className="btn btn-primary"
                  disabled
                  title="Aadhaar eSign integration deferred. Use POST /group/mous/:id/sign with a plain signature slot for now."
                >
                  <Icon name="file-signature" size={14} />Sign with Aadhaar eSign · soon
                </button>
                <button className="btn btn-ghost" onClick={() => setChangesOpen(true)}>Ask for changes</button>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* Aadhaar eSign drawer removed — real UIDAI eSign integration deferred.
          The Sign button above is disabled until then. */}

      <Drawer open={changesOpen} onClose={() => setChangesOpen(false)} title={`Ask ${partnerName} for changes`} size="md">
        <textarea className="textarea" rows={6} placeholder="Which clause, and what should change?" />
        <p className="helper m-0 mt-3">Their signature is withdrawn and they get a new draft to sign.</p>
        <div className="drawer-foot">
          <button
            className="btn btn-primary ml-auto"
            disabled
            title="Renegotiation request lands with MoU state-transition endpoints (draft-clauses + partner notify) in a follow-up. Use Renew for term-change only."
          >
            Send · soon
          </button>
        </div>
      </Drawer>
    </div>
  );
}
